import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Row,
  Form,
} from "react-bootstrap";
import FormField from "../../components/layout/FormField";
import { MotionDiv } from "../../components";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ModalTemplate from "../../components/modals/ModalTemplate";
import {
  useCreateProfMutation,
  useGetDomainsMutation,
  useGetProfByIdMutation,
  useGetSubdomainsMutation,
  useUpdateProfByIdMutation,
} from "../../features/apiSlice";
import { getError } from "../../utils/error";
import { FaRegRectangleXmark } from "react-icons/fa6";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import parsePhoneNumberFromString, { parsePhoneNumber } from "libphonenumber-js";
import { fetchSubdomains } from "../../utils/apis";

function AddEditProf() {
  const location = useLocation();
  const navigate = useNavigate();

  const isEditProf = location.pathname.includes("edit-prof");
  const [getDomain] = useGetDomainsMutation();
  const [getSubdomains] = useGetSubdomainsMutation();
  const [createProf, { isLoading }] = useCreateProfMutation();
  const [getProfById, { isLoading: getLoading }] = useGetProfByIdMutation();
  const [updateProfById, { isLoading: updateLoading }] =
    useUpdateProfByIdMutation();

  const [isFetchProfile, setIsFetchProfile] = useState(false);
  const [form, setForm] = useState({
    countryCode: "gb",
  });

  const [domains, setDomains] = useState([]);
  const [subdomains, setSubdomains] = useState([]);
  // selectedSubdomains elements: { sub_domain_name, _id, subdomain, account_id, pay_percent }
  const [selectedSubdomains, setSelectedSubdomains] = useState([]);

  const { id } = useParams();
  const [showAddModal, setShowAddModal] = useState(false);
  const handleShowAddModal = () => setShowAddModal(true);
  const handleHideAddModal = () => setShowAddModal(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const handleShowCancelModal = () => setShowCancelModal(true);
  const handleCloseCancelModal = () => setShowCancelModal(false);
  const [showCreatedModal, setShowCreatedModal] = useState(false);
  const handleShowCreatedModal = () => {
    setShowCreatedModal(true);
    setTimeout(() => handleHideCreatedModal(), 3000);
  };
  const handleHideCreatedModal = () => {
    setShowCreatedModal(false);
    navigate("/admin/profs");
  };

  const handleConfirmBtn = () => {
    handleSubmit();
  };

  const handleCancelConfirm = () => {
    handleCloseCancelModal();
    navigate(-1);
  };

  const fetchDomains = async () => {
    try {
      const data = await getDomain().unwrap();
      setDomains(data?.domains || []);
    } catch (error) {
      getError(error);
    }
  };

  const handleFetchSubdomains = async (domainId) => {
    try {
      // Reusing existing helper
      fetchSubdomains({ getSubdomains, setSubdomains, id: domainId });
    } catch (error) {
      getError(error);
    }
  };

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    if (form?.domain) {
      if (!isFetchProfile) {
        setSelectedSubdomains([]);
      }
      handleFetchSubdomains(form?.domain);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.domain]);

  useEffect(() => {
    if (id) {
      fetchProf();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // keep subdomains list updated by removing selected ones
  useEffect(() => {
    if (Array.isArray(selectedSubdomains)) {
      const selectedIds = selectedSubdomains.map((s) => s?._id);
      const updated = subdomains.filter((item) => !selectedIds.includes(item?._id));
      setSubdomains(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubdomains]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  // select handler for adding a subdomain
  const handleAddSubdomain = (e) => {
    // destructuring to remain compatible with your FormField select events
    const { name, label, value } = e.target;

    // avoid duplicates
    if (selectedSubdomains.some((s) => s._id === value)) return;

    const newItem = {
      sub_domain_name: label || (subdomains.find((s) => s._id === value)?.sub_domain_name || ""),
      _id: value,
      subdomain: value,
      account_id: "", // allow user to fill
      pay_percent: 0, // default 0, user must set
    };

    setSelectedSubdomains((prev) => [...prev, newItem]);
  };

  // remove selected subdomain and add it back to available list
  const handleDeleteSubdomain = (item) => {
    const updatedSelected = selectedSubdomains.filter((s) => s?._id !== item?._id);
    setSelectedSubdomains(updatedSelected);

    // add back to subdomains if not present already
    if (!subdomains.some((s) => s?._id === item?._id)) {
      setSubdomains((prev) => [
        ...(prev || []),
        { sub_domain_name: item?.sub_domain_name, _id: item?._id },
      ]);
    }
  };

  // handle inline edits to account_id or pay_percent for a selected subdomain
  const handleSubdomainFieldChange = (index, field, value) => {
    setSelectedSubdomains((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    handleShowAddModal();
  };

  const fetchProf = async () => {
    try {
      setIsFetchProfile(true);
      const data = await getProfById(id).unwrap();

      const prof = data?.professor;
      // populate top-level form fields
      setForm((prev) => ({
        ...prev,
        firstName: prof?.first_name,
        lastName: prof?.last_name,
        email: prof?.email,
        // legacy single pay_percent removed — we use per-subdomain
        dob: prof?.dob,
        doj: prof?.joining_date,
        mobile: prof?.mobile,
        id: prof?.professor_id,
        addr: prof?.address,
        domain: prof?.domain?._id,
      }));

      // set country code from mobile
      getCountryCode(prof?.mobile);

      // Map selected subdomains robustly (supports new and legacy shapes)
      let mappedSelected = [];

      if (Array.isArray(prof?.sub_domains) && prof.sub_domains.length > 0) {
        // new shape - array of objects { subdomain, account_id, pay_percent } or populated
        mappedSelected = prof.sub_domains.map((entry) => {
          // entry might be: { subdomain: ObjectId | populated object, account_id, pay_percent }
          const subObj = entry?.subdomain && typeof entry.subdomain === "object" ? entry.subdomain : null;
          const idVal = subObj?._id || entry?.subdomain || entry?._id || null;
          const name =
            subObj?.sub_domain_name ||
            entry?.sub_domain_name ||
            entry?.subdomain_name ||
            (subObj && (subObj.sub_domain_name || subObj.name)) ||
            "";


          return {
            sub_domain_name: name,
            _id: idVal,
            subdomain: idVal,
            account_id: entry?.account_id || "",
            pay_percent: entry?.pay_percent ?? 0,
          };
        });
      } else if (Array.isArray(prof?.sub_domain) && prof.sub_domain.length > 0) {
        // legacy shape (array of ids or populated)
        mappedSelected = prof.sub_domain.map((entry) => {
          const subObj = typeof entry === "object" ? entry : null;
          const idVal = subObj?._id || entry;
          const name =
            subObj?.sub_domain_name || subObj?.subdomain_name || subObj?.name || "";
          return {
            sub_domain_name: name,
            _id: idVal,
            subdomain: idVal,
            account_id:  "", // unknown in legacy
            pay_percent: prof?.pay_percent ?? 0, // fallback to single value
          };
        });
      }

      console.log('mp',mappedSelected)

      setSelectedSubdomains(mappedSelected);

      setIsFetchProfile(false);
    } catch (error) {
      setIsFetchProfile(false);
      getError(error);
    }
  };



  const getCountryCode = (phoneNumber) => {
    try {
      const parsedPhoneNumber = parsePhoneNumberFromString(`+${phoneNumber}`);
      if (parsedPhoneNumber) {
        setForm((prevForm) => ({
          ...prevForm,
          countryCode: parsedPhoneNumber?.country?.toLowerCase(),
        }));
      }
    } catch (error) {
      // ignore parse issues
      console.error("Error parsing phone number:", error);
    }
  };

  const validateEmail = (value) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(value);
  };

  const handleSubmit = async () => {
    // basic validations
    if (!form?.mobile) {
      toast.warn("Please enter mobile number.");
      return;
    }

    if (!validateEmail(form?.email)) {
      toast.warn("Enter a valid email");
      return;
    }

    // Validate each selected subdomain pay_percent
    const payloadSubDomains = selectedSubdomains.map((s) => ({
      subdomain: s.subdomain || s._id,
      account_id: s.account_id || "",
      pay_percent: Number(s.pay_percent || 0),
    }));

    const invalidPay = payloadSubDomains.some(
      (s) => isNaN(s.pay_percent) || s.pay_percent < 0 || s.pay_percent > 100
    );

    if (invalidPay) {
      toast.warn("Each subdomain pay percentage must be between 0 and 100");
      return;
    }

    // Validate phone number with selected country code
    try {
      const phoneNumber = parsePhoneNumber(form?.mobile, form?.countryCode?.toUpperCase());
      if (!(phoneNumber && phoneNumber.isValid())) {
        toast.warn("Enter a valid mobile number");
        return;
      }
    } catch (err) {
      toast.warn("Enter a valid mobile number");
      return;
    }

    // Build body
    const body = {
      first_name: form?.firstName,
      last_name: form?.lastName,
      email: form?.email,
      dob: form?.dob,
      joining_date: form?.doj,
      mobile: form?.mobile,
      professor_id: form?.id,
      address: form?.addr,
      domain: form?.domain,
      // new shape:
      sub_domains: payloadSubDomains,
    };

    // include password only on create
    if (!isEditProf && form?.password) {
      body.password = form.password;
    }

    try {
      const data = isEditProf
        ? await updateProfById({
            id,
            data: body,
          }).unwrap()
        : await createProf(body).unwrap();

      console.log("prof create/update response", data);
      handleHideAddModal();
      handleShowCreatedModal();
    } catch (error) {
      getError(error);
    }
  };

  return (
    <MotionDiv>
      <Container className="px-md-5">
        <h4 className="mb-3">{isEditProf ? "Edit Prof." : "Add New Prof."}</h4>
        <Form onSubmit={handleFormSubmit}>
          <Row>
            <Col md={6}>
              <Row>
                <FormField
                  label={"First Name"}
                  type={"text"}
                  placeholder={"First Name"}
                  name={"firstName"}
                  value={form?.firstName}
                  onChange={handleChange}
                />
              </Row>
              <Row>
                <FormField
                  label={"Last Name"}
                  type={"text"}
                  placeholder={"Last Name"}
                  name={"lastName"}
                  value={form?.lastName}
                  onChange={handleChange}
                />
              </Row>
              <Row>
                <FormField
                  label={"Email"}
                  type={"email"}
                  placeholder={"email@example.com"}
                  name={"email"}
                  value={form?.email}
                  onChange={handleChange}
                />
              </Row>
              <Row>
                <div className="form-group mb-3">
                  <label className="form-label d-block ">Phone Number</label>
                  <PhoneInput
                    containerClass="form-field ps-2"
                    inputClass=" w-100 m-0 border-0 rounded-4 form-field"
                    inputStyle={{ height: "2.3rem" }}
                    buttonClass="border-0 rounded-"
                    country={isEditProf ? "" : form?.countryCode?.toLowerCase()}
                    enableSearch={true}
                    countryCodeEditable={false}
                    value={form?.mobile}
                    onChange={(phone, code) => {
                      setForm({
                        ...form,
                        mobile: phone,
                        countryCode: code.countryCode,
                        dialCode: code.dialCode,
                      });
                    }}
                  />
                </div>
              </Row>
              <Row>
                <FormField
                  label={"Date Of Birth"}
                  type={"date"}
                  placeholder={"00/00/0000"}
                  name={"dob"}
                  dateMax={true}
                  value={form?.dob}
                  onChange={handleChange}
                />
              </Row>
              <Row>
                <FormField
                  label={"Address"}
                  type={"text"}
                  placeholder={"101 abc street"}
                  name={"addr"}
                  value={form?.addr}
                  onChange={handleChange}
                />
              </Row>
            </Col>
            <Col>
              <Row>
                <FormField
                  label={"Joining Date"}
                  placeholder={"00/00/0000"}
                  type={"date"}
                  name={"doj"}
                  value={form?.doj}
                  onChange={handleChange}
                />
              </Row>
              <Row>
                <FormField
                  label={" Domain"}
                  type={"select"}
                  name={"domain"}
                  value={form?.domain}
                  onChange={handleChange}
                  options={domains?.map((item) => ({
                    label: item?.domain_name,
                    value: item?._id,
                  }))}
                />
              </Row>
              <Row className="">
                <Col>
                  <FormField
                    label={" Specialty"}
                    type={"select"}
                    name={"subdomain"}
                    required={false}
                    onChange={handleAddSubdomain}
                    options={subdomains?.map((item) => ({
                      label: item?.sub_domain_name,
                      value: item?._id,
                    }))}
                  />

                  {/* Selected subdomains — now show account_id and pay_percent per item */}
                  {selectedSubdomains?.map((item, i) => (
                    <div
                      className="domain-card p-2 my-1 d-flex rounded"
                      style={{ color: "rgba(0, 0, 139, 1)" }}
                      key={item._id}
                    >
                      <div className="row w-100 align-items-center">
                        <div className="col-12 col-md-4">{item?.sub_domain_name || item?._id}</div>

                        <div className="col-12 col-md-6 d-flex gap-2 align-items-center mt-2 mt-md-0">
                          <input
                            className="form-control form-control-sm"
                            placeholder="Stripe Account ID (optional)"
                            value={item?.account_id}
                            onChange={(e) => handleSubdomainFieldChange(i, "account_id", e.target.value)}
                          />
                          <div className="d-flex align-items-center">
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              style={{ width: "5.5rem" }}
                              min="0"
                              max="100"
                              value={item?.pay_percent}
                              onChange={(e) => handleSubdomainFieldChange(i, "pay_percent", e.target.value)}
                            />
                            <span className="ms-1 fw-bold">%</span>
                          </div>
                        </div>

                        <div className="col-12 col-md-2 text-end mt-2 mt-md-0">
                          <FaRegRectangleXmark
                            onClick={() => handleDeleteSubdomain(item)}
                            style={{ cursor: "pointer" }}
                            color="rgba(255, 22, 22, 1)"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </Col>
              </Row>
            </Col>
          </Row>

          <hr />
          <Row>
            <Col md={6}>
              <FormField
                label={"Prof. ID"}
                type={"text"}
                placeholder={"Abc123"}
                name={"id"}
                value={form?.id}
                onChange={handleChange}
              />
            </Col>
            <Col>
              {isEditProf ? null : (
                <FormField
                  label={"Password"}
                  type={"password"}
                  placeholder={"********"}
                  name={"password"}
                  value={form?.password}
                  onChange={handleChange}
                />
              )}
            </Col>
          </Row>
          <Row>
            <Col className="text-end">
              <Button
                variant="transparent"
                className="del-btn px-3 m-1"
                onClick={handleShowCancelModal}
              >
                Cancel
              </Button>
              <Button className="blue-btn px-3 m-1" type="submit">
                {isEditProf ? "Save Details" : "Save Details"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>

      <ModalTemplate
        show={showCancelModal}
        onHide={handleCloseCancelModal}
        title={`Are you sure you want to cancel ${isEditProf ? "editing" : "adding"} professor?`}
        src={"/icons/question-red.png"}
        onDiscard={handleCloseCancelModal}
        onConfirm={handleCancelConfirm}
      />

      <ModalTemplate
        show={showAddModal}
        onHide={handleHideAddModal}
        src={"/icons/question-green.png"}
        title={`Are you sure you want to  ${isEditProf ? "update" : "add"} this Prof. profile to user system ?`}
        onConfirm={handleConfirmBtn}
        onDiscard={handleHideAddModal}
        loading={isLoading || updateLoading}
      />

      <ModalTemplate
        show={showCreatedModal}
        onHide={handleHideCreatedModal}
        src={"/icons/tick.png"}
        title={`You have successfully ${isEditProf ? "updated" : "created new"} Prof. profile`}
      />
    </MotionDiv>
  );
}

export default AddEditProf;
